import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Grid,
  Chip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  History as HistoryIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { AddressService } from '../../services/addressService';
import {
  CustomerAddress,
  AddressType,
  AddressHistory,
} from '../../types/address';
import AddressFormModal from './AddressFormModal';

interface AddressManagementProps {
  customerId: string;
  customerName: string;
}

const AddressManagement: React.FC<AddressManagementProps> = ({
  customerId,
  customerName,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<CustomerAddress | undefined>();
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAddress, setMenuAddress] = useState<CustomerAddress | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<CustomerAddress | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [addressHistory, setAddressHistory] = useState<AddressHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (customerId) {
      loadAddresses();
    }
  }, [customerId]);

  const loadAddresses = async () => {
    try {
      setLoading(true);
      console.log('[AddressManagement] Loading addresses for customer:', customerId);
      const response = await AddressService.getCustomerAddresses(customerId, {
        per_page: 100,
      });
      console.log('[AddressManagement] Loaded addresses:', response);
      
      // The backend returns 'addresses' field
      setAddresses(response?.addresses || []);
    } catch (error: any) {
      console.error('[AddressManagement] Failed to load addresses:', error);
      setAddresses([]); // Ensure addresses is always an array
      enqueueSnackbar(`Failed to load addresses: ${error.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadAddressHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await AddressService.getAddressHistory(customerId, undefined, {
        per_page: 50,
      });
      setAddressHistory(response.history || []);
    } catch (error: any) {
      enqueueSnackbar('Failed to load address history', { variant: 'error' });
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAddAddress = () => {
    setSelectedAddress(undefined);
    setFormMode('create');
    setFormModalOpen(true);
  };

  const handleEditAddress = (address: CustomerAddress) => {
    setSelectedAddress(address);
    setFormMode('edit');
    setFormModalOpen(true);
    handleMenuClose();
  };

  const handleSetDefault = async (address: CustomerAddress) => {
    try {
      await AddressService.setDefaultAddress(customerId, address.id);
      enqueueSnackbar('Default address updated successfully', { variant: 'success' });
      loadAddresses();
    } catch (error: any) {
      enqueueSnackbar('Failed to set default address', { variant: 'error' });
    }
    handleMenuClose();
  };

  const handleDeleteAddress = (address: CustomerAddress) => {
    setAddressToDelete(address);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDeleteAddress = async () => {
    if (!addressToDelete) return;

    try {
      await AddressService.deleteAddress(customerId, addressToDelete.id);
      enqueueSnackbar('Address deleted successfully', { variant: 'success' });
      loadAddresses();
      setDeleteDialogOpen(false);
      setAddressToDelete(null);
    } catch (error: any) {
      enqueueSnackbar('Failed to delete address', { variant: 'error' });
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, address: CustomerAddress) => {
    setAnchorEl(event.currentTarget);
    setMenuAddress(address);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuAddress(null);
  };

  const handleShowHistory = () => {
    setHistoryOpen(true);
    loadAddressHistory();
  };

  const getAddressTypeColor = (type: AddressType) => {
    switch (type) {
      case AddressType.BILLING:
        return 'primary';
      case AddressType.SHIPPING:
        return 'secondary';
      case AddressType.BOTH:
        return 'success';
      default:
        return 'default';
    }
  };

  const getAddressTypeLabel = (type: AddressType) => {
    switch (type) {
      case AddressType.BILLING:
        return 'Billing';
      case AddressType.SHIPPING:
        return 'Shipping';
      case AddressType.BOTH:
        return 'Billing & Shipping';
      default:
        return 'Address';
    }
  };

  const formatAddress = (address: CustomerAddress) => {
    const parts = [
      address.address_line1,
      address.address_line2,
      address.city,
      address.state,
      address.postal_code,
      address.country,
    ].filter(Boolean);
    return parts.join(', ');
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading addresses...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Address Management
          </Typography>
          <Chip
            label={`${addresses.length} address${addresses.length !== 1 ? 'es' : ''}`}
            size="small"
            variant="outlined"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={handleShowHistory}
            size="small"
          >
            History
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddAddress}
            color="primary"
          >
            Add Address
          </Button>
        </Box>
      </Box>

      {/* Addresses Grid */}
      {addresses.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <LocationIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No addresses found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add the first address for {customerName}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddAddress}
              color="primary"
            >
              Add Address
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {addresses.map((address) => (
            <Grid item xs={12} md={6} key={address.id}>
              <Card
                sx={{
                  position: 'relative',
                  border: address.is_default ? 2 : 1,
                  borderColor: address.is_default ? 'primary.main' : 'divider',
                  opacity: address.is_active ? 1 : 0.6,
                }}
              >
                <CardContent>
                  {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={getAddressTypeLabel(address.address_type)}
                        color={getAddressTypeColor(address.address_type)}
                        size="small"
                      />
                      {address.is_default && (
                        <Tooltip title="Default Address">
                          <StarIcon color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                      {!address.is_active && (
                        <Chip
                          label="Inactive"
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, address)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  {/* Address Details */}
                  <Typography variant="body2" color="text.primary" sx={{ mb: 2, lineHeight: 1.6 }}>
                    {formatAddress(address)}
                  </Typography>

                  {/* Contact Information */}
                  {(address.contact_person || address.contact_phone) && (
                    <Box sx={{ mb: 2 }}>
                      {address.contact_person && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Contact: {address.contact_person}
                        </Typography>
                      )}
                      {address.contact_phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {address.contact_phone}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* GSTIN */}
                  {address.gstin && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <BusinessIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        GSTIN: {address.gstin}
                      </Typography>
                    </Box>
                  )}

                  {/* Quick Actions */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditAddress(address)}
                      variant="outlined"
                    >
                      Edit
                    </Button>
                    {!address.is_default && (
                      <Button
                        size="small"
                        startIcon={<StarBorderIcon />}
                        onClick={() => handleSetDefault(address)}
                        variant="outlined"
                        color="warning"
                      >
                        Set Default
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => menuAddress && handleEditAddress(menuAddress)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Address</ListItemText>
        </MenuItem>
        {menuAddress && !menuAddress.is_default && (
          <MenuItem onClick={() => menuAddress && handleSetDefault(menuAddress)}>
            <ListItemIcon>
              <StarIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Set as Default</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => menuAddress && handleDeleteAddress(menuAddress)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Address</ListItemText>
        </MenuItem>
      </Menu>

      {/* Address Form Modal */}
      <AddressFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        onSuccess={loadAddresses}
        customerId={customerId}
        address={selectedAddress}
        mode={formMode}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Address</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          <Typography>
            Are you sure you want to delete this address?
          </Typography>
          {addressToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {formatAddress(addressToDelete)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteAddress} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address History Dialog */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            Address History for {customerName}
          </Box>
        </DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Typography>Loading history...</Typography>
          ) : addressHistory.length === 0 ? (
            <Typography color="text.secondary">No address history found.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Changed By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {addressHistory.map((history) => (
                    <TableRow key={history.id}>
                      <TableCell>
                        {new Date(history.changed_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={history.change_type}
                          size="small"
                          color={
                            history.change_type === 'created' ? 'success' :
                            history.change_type === 'updated' ? 'primary' :
                            history.change_type === 'deleted' ? 'error' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{history.description}</TableCell>
                      <TableCell>{history.changed_by_admin_id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddressManagement;